import { ReactNode, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import '../styles/FAQ.css';

const faqs: { question: string; answer: ReactNode }[] = [
    {
        question: "Do I need an account to request a quote?",
        answer: <p>No. Anyone can request a quote or send us a query. There is nothing to sign up for.</p>,
    },
    {
        question: "How long does the SAHPRA licensing process take?",
        answer: <p>The process typically takes 6 to 8 weeks, depending on the completeness of your application and SAHPRA review timelines.</p>,
    },
    {
        question: "What documents are required?",
        answer: (
            <>
                <p>You will need the following:</p>
                <ul>
                    <li>Director’s ID copy</li>
                    <li>Director’s CV</li>
                    <li>Company CK documents</li>
                    <li>Company letterhead</li>
                </ul>
            </>
        ),
    },
    {
        question: "Which licence do I need?",
        answer: (
            <p>
                It depends on what your business does. Distributors import, export or sell finished devices under
                their own name. Wholesalers buy devices from manufacturers or distributors to store, deliver and
                resell. Manufacturers produce the devices. If you are unsure, <Link to="/query">send us a query</Link> and
                we will advise you.
            </p>
        ),
    },
    {
        question: "Do I need prior experience to apply?",
        answer: <p>No, prior experience is not required. We guide you through the entire process step-by-step.</p>,
    },
];

// How long the pointer must rest on a question before it opens, so passing over doesn't open it
const HOVER_INTENT_MS = 120;
// Slightly longer than the open/close animation in FAQ.css
const HOLD_IN_PLACE_MS = 350;

// One answer open at a time. Hovering previews an answer; clicking (or tapping, or the keyboard)
// pins it open, which pauses the hover previews until it's clicked again.
const FAQ = () => {
    const baseId = useId();
    const [pinned, setPinned] = useState<number | null>(null);
    const [hovered, setHovered] = useState<number | null>(null);
    const [canHover] = useState(() => window.matchMedia("(hover: hover) and (pointer: fine)").matches);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const hoverTimer = useRef(0);
    const frame = useRef(0);

    const openIndex = pinned ?? hovered;

    useEffect(() => () => {
        window.clearTimeout(hoverTimer.current);
        cancelAnimationFrame(frame.current);
        document.documentElement.style.overflowAnchor = "";
    }, []);

    // Opening one answer closes the other. If the closing one sits above, everything below it
    // moves up, including the question under the pointer. Scroll by the same amount every frame
    // while the animation runs, so the item the visitor is pointing at stays still.
    const holdInPlace = (index: number) => {
        const item = itemRefs.current[index];
        if (!item) return;
        cancelAnimationFrame(frame.current);
        const root = document.documentElement;
        root.style.overflowAnchor = "none"; // so the browser's own scroll anchoring doesn't correct twice
        let lastTop = item.getBoundingClientRect().top + window.scrollY;
        let owed = 0; // scroll positions snap to whole pixels, so carry the fractions forward
        const until = performance.now() + HOLD_IN_PLACE_MS;
        const step = (now: number) => {
            const top = item.getBoundingClientRect().top + window.scrollY;
            owed += top - lastTop;
            lastTop = top;
            const whole = Math.round(owed);
            if (whole !== 0) {
                window.scrollBy(0, whole);
                owed -= whole;
            }
            if (now < until) frame.current = requestAnimationFrame(step);
            else root.style.overflowAnchor = "";
        };
        frame.current = requestAnimationFrame(step);
    };

    const startHover = (i: number) => {
        if (pinned !== null) return; // a pinned answer pauses hover previews
        window.clearTimeout(hoverTimer.current);
        hoverTimer.current = window.setTimeout(() => {
            holdInPlace(i);
            setHovered(i);
        }, HOVER_INTENT_MS);
    };

    const cancelHover = () => window.clearTimeout(hoverTimer.current);

    const leaveList = () => {
        window.clearTimeout(hoverTimer.current);
        setHovered(null);
    };

    const togglePin = (i: number) => {
        window.clearTimeout(hoverTimer.current);
        holdInPlace(i);
        setPinned(pinned === i ? null : i);
        setHovered(null);
    };

    return (
        <section className="km-section faqContainer" id="faq">
            <div className="km-container faq-inner">
                <div className="km-sectionHead km-sectionHead--center" data-reveal>
                    <p className="km-eyebrow">FAQ</p>
                    <h2 className="km-title">Frequently Asked Questions</h2>
                </div>
                <div className="faq-list" onMouseLeave={canHover ? leaveList : undefined}>
                    {faqs.map((faq, i) => {
                        const open = openIndex === i;
                        const isPinned = pinned === i;
                        const questionId = `${baseId}-q${i}`;
                        const answerId = `${baseId}-a${i}`;
                        return (
                            <div
                                key={faq.question}
                                ref={el => { itemRefs.current[i] = el; }}
                                className={`faq-item ${open ? "is-open" : ""} ${isPinned ? "is-pinned" : ""}`}
                                onMouseEnter={canHover ? () => startHover(i) : undefined}
                                onMouseLeave={canHover ? cancelHover : undefined}
                                data-reveal
                                style={{ "--reveal-order": i } as React.CSSProperties}
                            >
                                <h3 className="faq-question">
                                    <button
                                        type="button"
                                        id={questionId}
                                        aria-expanded={open}
                                        aria-controls={answerId}
                                        onClick={() => togglePin(i)}
                                    >
                                        <span className="faq-question-text">{faq.question}</span>
                                        {/* Hover-only hint: on touch screens a tap simply opens and closes */}
                                        {canHover && open && (
                                            <span className="faq-pin-hint" aria-hidden="true">
                                                {isPinned ? "Pinned" : "Click to pin"}
                                            </span>
                                        )}
                                        <span className="faq-icon" aria-hidden="true" />
                                    </button>
                                </h3>
                                <div className="faq-answer" id={answerId} role="region" aria-labelledby={questionId}>
                                    <div className="faq-answer-inner">{faq.answer}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default FAQ;
